import React, { useState, useEffect } from "react";
import { apiGet, apiPostPut, apiDelete } from "../../api/api_methods";
import { useStores } from "../../stores";
import { rolesWithId } from "../../constants/constants";

export default function Categories() {
  const { clientsStore } = useStores();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [fundOptions, setFundOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [editName, setEditName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFundOption, setNewFundOption] = useState({
    category: "",
    fundName: "",
    value: "",
    label: "",
  });

  // --- CATEGORY MANAGEMENT STATE ---
  const [categoryStats, setCategoryStats] = useState([]); // for stats if needed
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null); // string (category name)
  const [editCategoryName, setEditCategoryName] = useState("");

  // Wrap fetchCategories in useCallback to avoid changing on every render
  const fetchCategories = React.useCallback(async () => {
    try {
      setLoading(true);
      // Always include userId/userRole for admin endpoints
      const userId = clientsStore.user?._id;
      const userRole = clientsStore.user?.role;
      const response = await apiGet(
        `/fund-options/categories?includeStats=true&userId=${userId}&userRole=${userRole}`
      );
      if (response.status === 200 && response.body.success) {
        setCategories(response.body.data); // store array of objects
        setCategoryStats(response.body.data); // stats are in the same objects
        if (response.body.data.length > 0) {
          setSelectedCategory(response.body.data[0]._id);
        } else {
          setSelectedCategory("");
        }
        return response.body.data;
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
    return [];
  }, [clientsStore.user]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [clientsStore.user, fetchCategories]);

  // Fetch fund options when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchFundOptions(selectedCategory);
    }
  }, [selectedCategory]);

  // ...existing code...

  const fetchFundOptions = async (categoryId) => {
    try {
      setLoading(true);
      const response = await apiGet(
        `/fund-options/?category=${encodeURIComponent(categoryId)}`
      );
      if (response.status === 200 && response.body.success) {
        setFundOptions(response.body.data || []);
      }
    } catch (error) {
      console.error("Error fetching fund options:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFundOption = () => {
    setNewFundOption({
      category: selectedCategory,
      fundName: "",
      value: "",
      label: "",
    });
    setShowAddModal(true);
  };

  const handleSubmitNewFundOption = async () => {
    if (!newFundOption.fundName.trim()) {
      alert("Please enter a fund option name");
      return;
    }

    try {
      setLoading(true);
      const response = await apiPostPut(
        {
          category: newFundOption.category,
          fundName: newFundOption.fundName.trim(),
          value: newFundOption.fundName.trim(),
          label: newFundOption.fundName.trim(),
        },
        "/fund-options/create",
        "POST"
      );
      if (response.status === 200 && response.body.success) {
        setShowAddModal(false);
        setNewFundOption({
          category: "",
          fundName: "",
          value: "",
          label: "",
        });
        // Refresh the fund options list
        fetchFundOptions(selectedCategory);
      } else {
        alert("Failed to add fund option. Please try again.");
      }
    } catch (error) {
      console.error("Error adding fund option:", error);
      alert("Error adding fund option. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setShowAddModal(false);
    setNewFundOption({
      category: "",
      fundName: "",
      value: "",
      label: "",
    });
  };

  const handleEditOption = (option) => {
    setEditingOption(option);
    setEditName(option.fundName || option.name || option.title || "");
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      alert("Please enter a valid name");
      return;
    }

    try {
      setLoading(true);
      const response = await apiPostPut(
        { fundName: editName.trim() },
        `/fund-options/${editingOption._id}`,
        "PUT"
      );
      if (response.status === 200 && response.body.success) {
        setEditingOption(null);
        setEditName("");
        // Refresh the fund options list
        fetchFundOptions(selectedCategory);
      } else {
        alert("Failed to update fund option. Please try again.");
      }
    } catch (error) {
      console.error("Error updating fund option:", error);
      alert("Error updating fund option. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingOption(null);
    setEditName("");
  };

  const handleRemoveOption = async (optionId) => {
    if (window.confirm("Are you sure you want to remove this fund option?")) {
      try {
        setLoading(true);
        const response = await apiDelete(`/fund-options/${optionId}`);
        if (response.status === 200) {
          // Refresh the fund options list
          fetchFundOptions(selectedCategory);
        } else {
          alert("Failed to remove fund option. Please try again.");
        }
      } catch (error) {
        console.error("Error removing fund option:", error);
        alert("Error removing fund option. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // --- CATEGORY MANAGEMENT LOGIC ---
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert("Please enter a category name");
      return;
    }
    try {
      setLoading(true);
      const userId = clientsStore.user?._id;
      const userRole = clientsStore.user?.role;
      const response = await apiPostPut(
        { categoryName: newCategoryName.trim(), userId, userRole },
        "/fund-options/categories",
        "POST"
      );
      if (response.status === 200 && response.body.success) {
        setShowAddCategoryModal(false);
        setNewCategoryName("");
        fetchCategories();
      } else {
        alert(response.body.message || "Failed to add category");
      }
    } catch (error) {
      console.error("Error adding category:", error);
      alert("Error adding category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryName(category);
  };

  const handleSaveEditCategory = async () => {
    if (!editCategoryName.trim()) {
      alert("Please enter a valid category name");
      return;
    }
    try {
      setLoading(true);
      const userId = clientsStore.user?._id;
      const userRole = clientsStore.user?.role;
      const response = await apiPostPut(
        { categoryName: editCategoryName.trim(), userId, userRole },
        `/fund-options/categories/${encodeURIComponent(editingCategory)}`,
        "PUT"
      );
      if (response.status === 200 && response.body.success) {
        setEditingCategory(null);
        setEditCategoryName("");
        fetchCategories();
      } else {
        alert(response.body.message || "Failed to rename category");
      }
    } catch (error) {
      console.error("Error renaming category:", error);
      alert("Error renaming category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the category '${categoryId}'? This will soft-delete all fund options in this category.`
      )
    )
      return;
    try {
      setLoading(true);
      const userId = clientsStore.user?._id;
      const userRole = clientsStore.user?.role;
      const response = await apiDelete(
        `/fund-options/categories/${encodeURIComponent(
          categoryId
        )}?userId=${userId}&userRole=${userRole}`
      );
      if (response.status === 200 && response.body.success) {
        const newCategories = await fetchCategories();
        if (selectedCategory === categoryId) {
          if (newCategories && newCategories.length > 0) {
            setSelectedCategory(newCategories[0]._id);
          } else {
            setSelectedCategory("");
          }
        }
        // No alert on success
      } else {
        // No alert on failure, just log
        console.error(response.body.message || "Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      // No alert on error
    } finally {
      setLoading(false);
    }
  };

  // Admin check
  if (clientsStore.user?.role !== rolesWithId.admin) {
    return null;
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Fund Options Management
      </h2>

      {/* CATEGORY MANAGEMENT SECTION */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Categories</h3>
          <button
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
            onClick={() => setShowAddCategoryModal(true)}
            disabled={loading}
          >
            + Add Category
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((categoryObj) => {
                const stats = categoryStats.find(
                  (c) => c._id === categoryObj._id
                );
                return (
                  <tr key={categoryObj._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {editingCategory === categoryObj._id ? (
                        <input
                          type="text"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="border px-2 py-1 rounded w-48"
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleSaveEditCategory()
                          }
                        />
                      ) : (
                        <span
                          className="cursor-pointer font-medium"
                          onClick={() => setSelectedCategory(categoryObj._id)}
                        >
                          {categoryObj._id}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {stats && stats.count !== undefined
                        ? `Count: ${stats.count}`
                        : "-"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {editingCategory === categoryObj._id ? (
                        <>
                          <button
                            className="text-green-600 hover:text-green-900 px-2 py-1 rounded"
                            onClick={handleSaveEditCategory}
                            disabled={loading}
                          >
                            Save
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-900 px-2 py-1 rounded"
                            onClick={() => setEditingCategory(null)}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded"
                            onClick={() => handleEditCategory(categoryObj._id)}
                            disabled={loading}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 px-2 py-1 rounded"
                            onClick={() =>
                              handleDeleteCategory(categoryObj._id)
                            }
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Add New Category
            </h3>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddCategoryModal(false)}
                disabled={loading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={loading || !newCategoryName.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {loading ? "Adding..." : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Selection and Add Button */}
      <label
        htmlFor="category-select"
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        Select Category
      </label>
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <select
            id="category-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            {categories.map((categoryObj) => (
              <option key={categoryObj._id} value={categoryObj._id}>
                {categoryObj._id}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleAddFundOption}
          disabled={loading || !selectedCategory}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Fund
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Fund Options Table */}
      {!loading && selectedCategory && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Fund Options for: {selectedCategory}
          </h3>

          {fundOptions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No fund options found for this category.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fund Option Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fundOptions.map((option, index) => (
                    <tr key={option._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingOption && editingOption._id === option._id ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) =>
                              e.key === "Enter" && handleSaveEdit()
                            }
                          />
                        ) : (
                          <span className="text-sm text-gray-900">
                            {option.fundName ||
                              option.name ||
                              option.title ||
                              (typeof option === "string"
                                ? option
                                : "Unnamed Option")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingOption && editingOption._id === option._id ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={handleSaveEdit}
                              className="text-green-600 hover:text-green-900 px-2 py-1 rounded"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-gray-600 hover:text-gray-900 px-2 py-1 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditOption(option)}
                              className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleRemoveOption(option._id)}
                              className="text-red-600 hover:text-red-900 px-2 py-1 rounded"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Fund Option Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Add New Fund Option
            </h3>

            <div className="space-y-4">
              {/* Category Select */}
              <div>
                <label
                  htmlFor="modal-category"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category
                </label>
                <select
                  id="modal-category"
                  value={newFundOption.category}
                  onChange={(e) =>
                    setNewFundOption({
                      ...newFundOption,
                      category: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Select a category</option>
                  {categories.map((categoryObj) => (
                    <option key={categoryObj._id} value={categoryObj._id}>
                      {categoryObj._id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fund Option Name */}
              <div>
                <label
                  htmlFor="modal-fund-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Fund Option Name
                </label>
                <input
                  type="text"
                  id="modal-fund-name"
                  value={newFundOption.fundName}
                  onChange={(e) =>
                    setNewFundOption({
                      ...newFundOption,
                      fundName: e.target.value,
                    })
                  }
                  placeholder="Enter fund option name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancelAdd}
                disabled={loading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitNewFundOption}
                disabled={
                  loading ||
                  !newFundOption.category ||
                  !newFundOption.fundName.trim()
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Adding..." : "Add Fund Option"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
